/**
 * Keeps the ghost registry (ghosts.ts) in step with the streaming assistant
 * message: reserve a block per write tool call, grow it as items stream in,
 * fade it when the call fails, is denied or ends without a canvas action,
 * and pan to a block whose first ghost starts off screen (ghostCamera.ts).
 * The ghost layer calls it from an effect; the dev page and tests call it
 * directly.
 */
import { diagramPositions, moveBlockToOpenSpot } from '@/lib/partnrActionExecutor';
import { visibleCanvasRect } from '@/lib/partnrActionExecutor';
import { useCanvasStore } from '@/stores/canvasStore';
import { blockSize, columnsThatFit, expectedCount, GHOST_TOOLS, ghostObstacles, ghostSpecs, layoutFor, usePartnrGhostStore } from './ghosts';
import { revealGhostBlock } from './ghostCamera';
import type { PartnrUIMessage } from './types';

const RELATIONS = new Set(['top', 'bottom', 'left', 'right']);
/** Canvas pixels kept free between a new block and the edge of the view */
const EDGE = 32;

/**
 * Pulls a block that would start off the left or top of the view back in,
 * unless that spot is taken: an open spot out of view beats covering cards.
 */
function clampInto(origin: { x: number; y: number }, size: { width: number; height: number }, visible: { x: number; y: number; width: number; height: number }) {
  const x = size.width <= visible.width - 2 * EDGE ? Math.min(Math.max(origin.x, visible.x + EDGE), visible.x + visible.width - EDGE - size.width) : visible.x + EDGE;
  const y = Math.max(origin.y, visible.y + EDGE);
  if (x === origin.x && y === origin.y) return origin;
  const items = useCanvasStore.getState().items.filter((i) => !i.isGhost).map((i) => ({ x: i.position_x, y: i.position_y, width: i.width, height: i.height }));
  const taken = [...items, ...ghostObstacles(usePartnrGhostStore.getState().blocks)];
  const hits = taken.some((r) => x < r.x + r.width + 20 && r.x < x + size.width + 20 && y < r.y + r.height + 20 && r.y < y + size.height + 20);
  return hits ? origin : { x, y };
}

type ToolPart = { type: string; toolCallId: string; state: string; input?: unknown; output?: { canvasAction?: unknown } };

function toolPartsOf(message: PartnrUIMessage | null): ToolPart[] {
  if (!message) return [];
  return message.parts.filter((p): p is typeof p & ToolPart => p.type.startsWith('tool-') && typeof (p as { toolCallId?: unknown }).toolCallId === 'string') as ToolPart[];
}

/** Where a new block goes: beside the item the tool names, else open space in view. */
function reserveOrigin(toolName: string, input: Record<string, unknown>, size: { width: number; height: number }): { x: number; y: number } {
  const placement = input.placement as { relative_to_item?: unknown; relation?: unknown } | undefined;
  const anchor = typeof placement?.relative_to_item === 'string' ? placement.relative_to_item : undefined;
  const relation = typeof placement?.relation === 'string' && RELATIONS.has(placement.relation) ? placement.relation : 'right';
  const moved = moveBlockToOpenSpot(
    [{ id: 'block', ...size }],
    new Map([['block', { x: 0, y: 0 }]]),
    anchor ? { strategy: 'relative_to_item', relation, resolvedItemId: anchor } : undefined,
  );
  return moved.get('block') ?? { x: 0, y: 0 };
}

/** Applies one look at the streaming message to the registry. `laidOut` remembers diagrams already moved to their final layout. */
export function syncGhostsFromMessage(message: PartnrUIMessage | null, boardId: string | null, laidOut: Set<string>): void {
  const ghosts = usePartnrGhostStore.getState();
  for (const part of toolPartsOf(message)) {
    const toolName = part.type.slice('tool-'.length);
    if (!GHOST_TOOLS.has(toolName)) continue;
    const key = part.toolCallId;
    if (part.state === 'output-error' || part.state === 'output-denied') {
      ghosts.fade(key);
      continue;
    }
    if (part.state === 'output-available') {
      if (!part.output?.canvasAction) ghosts.fade(key);
      continue;
    }
    if (part.state !== 'input-streaming' && part.state !== 'input-available') continue;

    const complete = part.state === 'input-available';
    const input = (part.input && typeof part.input === 'object' ? part.input : {}) as Record<string, unknown>;
    const { specs, edges } = ghostSpecs(toolName, input, complete);
    if (specs.length === 0) continue;

    if (!usePartnrGhostStore.getState().blocks[key]) {
      // As many per row as fit in the part of the canvas the user can see
      const visible = visibleCanvasRect();
      const typical = layoutFor(toolName, input);
      const layout = { ...typical, columns: columnsThatFit(typical, visible.width - 2 * EDGE) };
      const size = blockSize(layout, Math.max(specs.length, expectedCount(toolName)));
      const origin = clampInto(reserveOrigin(toolName, input, size), size, visible);
      ghosts.reserve({ key, toolName, boardId, origin, size, columns: layout.columns, cell: layout.cell });
    }
    ghosts.update(key, specs, edges, complete);
    // An open spot can be off screen on a crowded view: the first ghost brings it into view, once
    const live = usePartnrGhostStore.getState().blocks[key];
    if (live) revealGhostBlock(live);

    // A finished diagram moves its ghosts once to the layout the nodes will land on
    if (complete && toolName === 'create_diagram' && !laidOut.has(key)) {
      laidOut.add(key);
      const nodes = (Array.isArray(input.nodes) ? input.nodes : []) as Array<{ id: string; item_type: string; title: string }>;
      const diagramEdges = (Array.isArray(input.edges) ? input.edges : []) as Array<{ source_id: string; target_id: string }>;
      const positions = diagramPositions(nodes, diagramEdges, typeof input.diagram_type === 'string' ? input.diagram_type : 'flowchart');
      const block = usePartnrGhostStore.getState().blocks[key];
      const points = nodes.map((n) => positions.get(n.id));
      const minX = Math.min(...points.map((p) => p?.x ?? Infinity));
      const minY = Math.min(...points.map((p) => p?.y ?? Infinity));
      if (block && Number.isFinite(minX) && Number.isFinite(minY)) {
        ghosts.moveSlots(key, points.map((p) => (p ? { x: block.origin.x + p.x - minX, y: block.origin.y + p.y - minY } : undefined)));
      }
    }
  }
}
